import { useEffect, useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { shortcutFor } from './shortcuts';
import type { ShortcutAction } from './shortcuts';

export interface ShortcutHandlers {
  onShading: (index: number) => void;
  onZoom: () => void;
  onFit: () => void;
  onProjection: () => void;
  onMeasure: () => void;
  onSectionSize: (grow: boolean) => void;
  /** Steps back one layer (tool, selection, section); false when there was nothing to step back from. */
  onEscape: () => boolean;
}

const REPEATABLE = new Set<ShortcutAction['kind']>(['sectionSize']);

function isEditable(node: EventTarget | undefined): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return node.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(node.tagName);
}

function dispatch(action: ShortcutAction, handlers: ShortcutHandlers): void {
  switch (action.kind) {
    case 'shading':
      return handlers.onShading(action.index);
    case 'zoom':
      return handlers.onZoom();
    case 'fit':
      return handlers.onFit();
    case 'projection':
      return handlers.onProjection();
    case 'measure':
      return handlers.onMeasure();
    case 'sectionSize':
      return handlers.onSectionSize(action.grow);
    default:
      return undefined;
  }
}

/**
 * The viewer's keys, live while the pointer is over the viewer or focus is in
 * it, so two viewers on one page never both answer. An Esc the viewer used is
 * marked handled (default prevented) so a dialog around it stays open.
 */
export function useViewerShortcuts(
  wrapperRef: RefObject<HTMLElement | null>,
  handlers: ShortcutHandlers,
  enabled: boolean,
  openPopoversRef: MutableRefObject<number>,
): void {
  const handlersRef = useRef(handlers);
  useLayoutEffect(() => {
    handlersRef.current = handlers;
  });
  const overRef = useRef(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!enabled || !wrapper) return undefined;
    // The pointer may already rest on the viewer as its keys turn on (WebKit sends no pointerenter then).
    overRef.current = wrapper.matches(':hover');
    const enter = () => {
      overRef.current = true;
    };
    const leave = () => {
      overRef.current = false;
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const origin = event.composedPath()[0];
      if (isEditable(origin)) return;
      const focused = origin instanceof Node && wrapper.contains(origin);
      if (!overRef.current && !focused) return;
      const action = shortcutFor(event.key);
      if (!action) return;
      if (event.repeat && !REPEATABLE.has(action.kind)) return;
      if (action.kind === 'escape') {
        if (openPopoversRef.current > 0 || handlersRef.current.onEscape()) event.preventDefault();
        return;
      }
      event.preventDefault();
      dispatch(action, handlersRef.current);
    };
    wrapper.addEventListener('pointerenter', enter);
    wrapper.addEventListener('pointerleave', leave);
    // Capture, so the viewer marks an Esc it used before a dialog around it hears it,
    // whichever of the two started listening first.
    document.addEventListener('keydown', onKey, true);
    return () => {
      wrapper.removeEventListener('pointerenter', enter);
      wrapper.removeEventListener('pointerleave', leave);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [wrapperRef, enabled, openPopoversRef]);
}
